/**
 * TaxSavingsSummary Component
 * 
 * Displays key financial metrics in a gradient card layout.
 * Shows total tax savings, ROI, and subscription investment.
 * 
 * Features:
 * - Gradient background (blue to teal)
 * - Three metric cards in responsive grid
 * - Large numbers with descriptive text
 * - Motivational footer message
 * - Responsive design (stacks on mobile)
 * 
 * Created: 2025-01-16 (Dashboard Redesign Phase 2)
 * Phase: Dashboard Redesign - Phase 2: Content Cards
 */

import React from 'react';

const TaxSavingsSummary = ({ totalSavings, roi, investment }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="bg-gradient-to-r from-blue-500 to-teal-500 rounded-xl shadow-lg p-6 text-white">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Your Tax Savings Impact</h2>
        <p className="text-blue-100 text-sm">
          Track your financial progress with BizHub strategies
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        
        {/* Total Savings */}
        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-blue-100">Total Savings</span>
          </div>
          <div className="text-3xl font-bold">{formatCurrency(totalSavings)}</div>
          <p className="text-xs text-blue-100 mt-1">Year-to-date</p>
        </div>

        {/* ROI */}
        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <span className="text-sm font-medium text-blue-100">Return on Investment</span>
          </div>
          <div className="text-3xl font-bold">{roi}</div>
          <p className="text-xs text-blue-100 mt-1">Your subscription ROI</p>
        </div>

        {/* Investment */}
        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-30">
          <div className="flex items-center mb-2">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <span className="text-sm font-medium text-blue-100">Annual Investment</span>
          </div>
          <div className="text-3xl font-bold">{formatCurrency(investment)}</div>
          <p className="text-xs text-blue-100 mt-1">Premium subscription</p>
        </div>
      </div>

      {/* Footer Message */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
        <div className="flex items-start">
          <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold mb-1">Outstanding Progress!</p>
            <p className="text-xs text-blue-100">
              You've saved {formatCurrency(totalSavings)} this year through strategic tax planning. 
              That's a {roi} return on your {formatCurrency(investment)} investment with BizHub.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Default props for when no data is provided
TaxSavingsSummary.defaultProps = {
  totalSavings: 45650,
  roi: '12.8x',
  investment: 3200,
};

export default TaxSavingsSummary;
